import { getNextStaticProps } from "@faustjs/next";
import { GetStaticPropsContext } from "next";
import { useMenu } from "hooks";
import { client, PageIdType } from "client";
import Layout from "layouts/main";
import {
  HomepageHero,
  WhoWeAreCapabilities,
  VideoSection,
  HomeSocialProof,
  CTASection,
} from "components";
import ColorBand from "components/atoms/svgs/ColorBand";
import { BuildingOfficeIcon, CpuChipIcon } from "@heroicons/react/24/outline";

export default function Page() {
  const { usePage } = client;
  const headerLinks = useMenu("PRIMARY");
  const footerLinks = useMenu("FOOTER");
  const page = usePage({
    id: `/`,
    idType: PageIdType.URI,
  });

  return (
    <Layout
      page={page}
      headerLinks={headerLinks}
      footerLinks={footerLinks}
      transparentHeader
      lightLogo
    >
      {/* Hero Section */}
      <HomepageHero
        backgroundImage="/images/crafted-office-background.jpg"
        headline="Build Digital Experiences People Love, Faster Than Ever Before"
        bracketedText="Faster Than Ever Before"
        bracketAnimateColors={true}
        subheadline="Crafted is a development partner for organizations that refuse to settle for slow, fragmented, or mediocre software. We pair refined taste, deep technical expertise, and AI-native execution to help you design, build, and ship exceptional digital products."
        ctaButtons={[
          {
            text: "For Agencies",
            href: "/agencies",
            icon: BuildingOfficeIcon,
            variant: "primary",
          },
          {
            text: "For Product & Tech Teams",
            href: "/for-product-marketing-teams",
            icon: CpuChipIcon,
            variant: "secondary",
          },
        ]}
      />

<div className="w-full h-[4px]">
       			 <ColorBand className="w-full h-full block" />
      		</div>

      {/* Who We Are + Capabilities Section */}
      <WhoWeAreCapabilities
        headline="A Development Partner Built for Today's Software Challenges"
        description={`Software now needs to be beautiful, intuitive, fast, and constantly evolving. Most teams can't keep up — not because they aren't smart, but because the work now spans product strategy, UX, engineering, DevOps, QA, and AI-accelerated workflows.

Crafted brings all of that together in one senior, scalable team.`}
        capabilities={[
          {
            title: "Product Leadership",
            description:
              "Roadmaps, technical strategy, phased builds, architecture, prioritization.",
          },
          {
            title: "UX & Digital Experience",
            description:
              "Design systems, UI/UX, interaction patterns, accessibility, user flows.",
          },
          {
            title: "Engineering",
            description:
              "Web apps, mobile apps, backend systems, integrations, infrastructure, performance.",
          },
          {
            title: "AI-Native Execution",
            description:
              "Modern development workflows that dramatically increase velocity without sacrificing quality.",
          },
          {
            title: "QA & Reliability",
            description:
              "Ticketed process, automated + manual testing, device coverage, clean releases.",
          },
          {
            title: "Ongoing Partnership",
            description:
              "Continuous improvement, analytics, iteration, and long-term digital stewardship.",
          },
        ]}
        tagline="One partner. Every capability you need. Delivered with discipline, taste, and velocity."
      />

      {/* Video Section */}
      <VideoSection
        videoType="self-hosted"
        videoSrc="https://assets.craftedup.com/SeanHiseSpeakerReel.mp4"
        bgColor="bg-gray-100"
      />

      {/* Social Proof Section */}
      <HomeSocialProof
        sectionTitle="Proven Across Industries. Chosen by Teams Who Expect More."
        testimonials={[
          {
            quote:
              "With all the new innovations that are available Crafted has been able to streamline my business process and make it affordable, exciting and attainable.",
            title: "Matt Hayes, CEO",
            company: "Lunchtime MD",
          },
          {
            quote:
              "Crafted has always been great to work with in the development of our mobile App.  They are responsive, thoughtful and have embraced the project our agency is undertaking.  They helped us create the vision for our App and we enjoy having them as our partner.  They go above and beyond to make sure our needs are met and help to move our agency forward.",
            title: "Amy Essley, Chief Program Officer",
            company: "Big Brothers Big Sisters Central Indiana",
          },
        ]}
      />

     

      {/* CTA Section - copy kept in page for easy edits */}
      <CTASection
        body="Book a no-pressure discovery call. We'll review your product goals, diagnose bottlenecks, and see if we're the right fit."
        ctaText="Talk to Crafted"
      />

       {/* Color band divider - SVG scales to fill width and height */}
       <div className="w-full h-[4px]">
        <ColorBand className="w-full h-full block" />
      </div>
    </Layout>
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  return getNextStaticProps(context, {
    Page,
    client,
  });
}
